/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

import React from 'react';
import { connect } from 'react-redux';
import MetadataState from '../enums/MetadataState';
import { LayoutComponent } from './LayoutComponent';
import MetadataLoadingOverlayComponent from './MetadataLoadingOverlayComponent';
import MetadataErrorMessageComponent from './MetadataErrorMessageComponent';
import {
    loadMetadata as loadMetadataAction,
    publishMetadataInvalid as publishMetadataInvalidAction
} from '../actions/metadataActions';
import { IState, IStateMetadataError } from '../store/types/StateTypes';
import generateErrorMessage from '../validators/ErrorDescriptorService';
import { IPropertiesValidator } from '../validators/ValidatorTypes';
import { cast, Nullable } from '../store/types/CommonTypes';
import DataState from '../enums/DataState';
import DataErrorMessageComponent from './DataErrorMessageComponent';

interface IMetadataServiceComponentProperties {
    adHocViewHeight: number,
    server: string,
    resourceURI: string,
    metadataState: string,
    metadataError: Nullable<IStateMetadataError>,
    dataState: string,
    dataError: any,
    deferredAction: JQuery.Deferred<any>,
    validator: Nullable<IPropertiesValidator>,

    loadMetadata: Function,
    publishMetadataInvalid: Function
}

class MetadataServiceComponentUnconnected extends React.Component<IMetadataServiceComponentProperties, never> {
    componentDidMount() {
        this.invokeService();
    }

    componentDidUpdate(prevProps: any) {
        if (this.props.metadataState !== prevProps.metadataState) {
            this.invokeService();
        }
    }

    invokeService() {
        let error: any = null;
        const {
            server,
            resourceURI,
            loadMetadata,
            metadataState,
            metadataError,
            deferredAction,
            publishMetadataInvalid,
            validator
        } = this.props;

        if (metadataState === MetadataState.LOADED) {
            error = validator && validator.validate(null);
            if (error) {
                publishMetadataInvalid(error);
            }
        } else if (metadataState === MetadataState.LOADING) {
            loadMetadata(server, resourceURI);
        } else if (metadataState === MetadataState.ERROR) {
            deferredAction.reject(metadataError);
        } else if (metadataState === MetadataState.INVALID) {
            deferredAction.reject(metadataError);
        } else if (metadataState === MetadataState.DESTROYED) {
            deferredAction.resolve();
        }
    }

    render() {

        let content = null;
        const {
            adHocViewHeight,
            metadataState,
            metadataError,
            dataState,
            dataError,
            deferredAction
        } = this.props;

        if (metadataState === MetadataState.LOADED) {
            if (dataState === DataState.ERROR) {
                content = <DataErrorMessageComponent error={generateErrorMessage(dataError)} />;

                // Any data service component will not be able to react on Data Error (and reject deferredAction) because
                // we are rendering Metadata and Data errors here (without calling the LayoutComponent), so we have to
                // reject deferredAction here:
                deferredAction.reject(dataError);
            } else {
                content = <LayoutComponent />;
            }
        } else if (metadataState === MetadataState.LOADING) {
            content = <MetadataLoadingOverlayComponent adHocViewHeight={adHocViewHeight} />;
        } else if (metadataState === MetadataState.ERROR || metadataState === MetadataState.INVALID) {
            content = <MetadataErrorMessageComponent error={generateErrorMessage(cast(metadataError))} />;
        }

        return content;
    }
}

const mapStateToProps = (state: IState) => ({
    server: state.server,
    resourceURI: state.resourceURI,
    metadataState: state.metadataState,
    metadataError: state.metadataError,
    dataState: state.dataState,
    dataError: state.dataError,
    adHocViewHeight: state.adHocViewHeight,
    deferredAction: state.deferredAction
});

const mapDispatchToProps = (dispatch: any) => ({
    loadMetadata: (server: string, uri: string) => (dispatch(loadMetadataAction(server, uri))),
    publishMetadataInvalid: (error: any) => (dispatch(publishMetadataInvalidAction(error)))
});

const connectFn = connect(mapStateToProps, mapDispatchToProps);
const MetadataServiceComponent = connectFn(MetadataServiceComponentUnconnected);

export {
    MetadataServiceComponent,
    MetadataServiceComponentUnconnected,
    mapDispatchToProps,
    mapStateToProps
};
